"use client";

import { useEffect, useRef, useState } from "react";
import styles from "./HeroArborCard.module.css";

type ThreeModule = typeof import("three");
type ArborHandle = { setTheme: (mode: "light" | "dark") => void; dispose: () => void };

/**
 * Builds the live 3D arbor scene inside the supplied canvas. This is a direct
 * port of the OptiStackArbor IIFE from optistack-hero-card-3d.html: the studio
 * environment, materials, lathe-built spacers/knives, the extent-based camera
 * fit (dV/dW), the staggered assemble + counter-rotate animation, and the
 * prefers-reduced-motion static fallback are all preserved.
 */
function createArbor(
  THREE: ThreeModule,
  canvas: HTMLCanvasElement,
  wrap: HTMLElement,
  vpload: HTMLElement
): ArborHandle {
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.outputEncoding = THREE.sRGBEncoding;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  let exposure = 1.12;
  renderer.toneMappingExposure = exposure;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(30, 2, 0.1, 400);

  /* Studio environment, neutral so it reads on light and dark panels */
  function makeEnv() {
    const c = document.createElement("canvas");
    c.width = 1024;
    c.height = 512;
    const x = c.getContext("2d")!;
    const g = x.createLinearGradient(0, 0, 0, 512);
    g.addColorStop(0.0, "#11151c");
    g.addColorStop(0.3, "#566173");
    g.addColorStop(0.5, "#262d38");
    g.addColorStop(1.0, "#0b0e13");
    x.fillStyle = g;
    x.fillRect(0, 0, 1024, 512);
    const sb = x.createLinearGradient(0, 40, 0, 170);
    sb.addColorStop(0, "rgba(255,255,255,0)");
    sb.addColorStop(0.5, "rgba(255,255,255,0.95)");
    sb.addColorStop(1, "rgba(255,255,255,0)");
    x.fillStyle = sb;
    x.fillRect(70, 40, 580, 130);
    const rg = x.createRadialGradient(824, 372, 8, 824, 372, 260);
    rg.addColorStop(0, "rgba(232,116,44,0.40)");
    rg.addColorStop(1, "rgba(232,116,44,0)");
    x.fillStyle = rg;
    x.fillRect(0, 0, 1024, 512);
    const tex = new THREE.Texture(c);
    tex.needsUpdate = true;
    tex.mapping = THREE.EquirectangularReflectionMapping;
    const pm = new THREE.PMREMGenerator(renderer);
    const env = pm.fromEquirectangular(tex).texture;
    tex.dispose();
    pm.dispose();
    return env;
  }
  scene.environment = makeEnv();

  function noiseTex(rep: number) {
    const c = document.createElement("canvas");
    c.width = 256;
    c.height = 256;
    const ctx = c.getContext("2d")!;
    const img = ctx.createImageData(256, 256);
    for (let i = 0; i < img.data.length; i += 4) {
      const v = 168 + Math.random() * 74;
      img.data[i] = v;
      img.data[i + 1] = v;
      img.data[i + 2] = v;
      img.data[i + 3] = 255;
    }
    ctx.putImageData(img, 0, 0);
    const t = new THREE.CanvasTexture(c);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(rep, rep);
    return t;
  }

  const matShaft = new THREE.MeshStandardMaterial({ color: 0x8d949c, metalness: 1.0, roughness: 0.38, envMapIntensity: 1.1 });
  matShaft.roughnessMap = noiseTex(8);
  const matKnife = new THREE.MeshStandardMaterial({ color: 0xccd2d9, metalness: 1.0, roughness: 0.1, envMapIntensity: 1.34 });
  const matMetal = new THREE.MeshStandardMaterial({ color: 0x9aa1a9, metalness: 1.0, roughness: 0.34, envMapIntensity: 1.15 });
  matMetal.roughnessMap = noiseTex(3);
  const matHub = new THREE.MeshStandardMaterial({ color: 0x6f757d, metalness: 1.0, roughness: 0.46, envMapIntensity: 0.95 });
  const matRubber = new THREE.MeshStandardMaterial({ color: 0x16a657, metalness: 0.0, roughness: 0.85, envMapIntensity: 0.4, emissive: 0x0c3a22, emissiveIntensity: 0.0 });
  const matClamp = new THREE.MeshStandardMaterial({ color: 0x636970, metalness: 1.0, roughness: 0.5, envMapIntensity: 0.9 });
  const matMark = new THREE.MeshStandardMaterial({ color: 0xe8742c, metalness: 0.2, roughness: 0.55 });

  const shaftR = 0.5;
  const bore = shaftR + 0.02;
  const knifeR = 1.55;
  const metalR = 0.85;
  const rubberR = 0.9;
  const rHub = 0.62;
  const clampR = 1.0;
  const W = 0.02;

  function knife(t: number) {
    const ht = t / 2;
    const bt = ht * 0.55;
    const hubR = bore + 0.2;
    const fR = knifeR - 0.05;
    const pts = [
      new THREE.Vector2(bore, ht), new THREE.Vector2(hubR, ht),
      new THREE.Vector2(hubR, bt), new THREE.Vector2(fR, bt),
      new THREE.Vector2(knifeR, 0.004), new THREE.Vector2(fR, -bt),
      new THREE.Vector2(hubR, -bt), new THREE.Vector2(hubR, -ht),
      new THREE.Vector2(bore, -ht), new THREE.Vector2(bore, ht),
    ];
    const geo = new THREE.LatheGeometry(pts, 96);
    geo.computeVertexNormals();
    const m = new THREE.Mesh(geo, matKnife);
    m.rotation.z = Math.PI / 2;
    m.castShadow = true;
    m.receiveShadow = true;
    return m;
  }
  function sleeve(Rin: number, Rout: number, w: number, mat: THREE.Material, ch?: number) {
    ch = ch || 0;
    const hw = w / 2;
    let pts;
    if (ch > 0) {
      pts = [
        new THREE.Vector2(Rin, hw), new THREE.Vector2(Rout - ch, hw),
        new THREE.Vector2(Rout, hw - ch), new THREE.Vector2(Rout, -hw + ch),
        new THREE.Vector2(Rout - ch, -hw), new THREE.Vector2(Rin, -hw), new THREE.Vector2(Rin, hw),
      ];
    } else {
      pts = [
        new THREE.Vector2(Rin, hw), new THREE.Vector2(Rout, hw),
        new THREE.Vector2(Rout, -hw), new THREE.Vector2(Rin, -hw), new THREE.Vector2(Rin, hw),
      ];
    }
    const geo = new THREE.LatheGeometry(pts, 72);
    geo.computeVertexNormals();
    const m = new THREE.Mesh(geo, mat);
    m.rotation.z = Math.PI / 2;
    m.castShadow = true;
    m.receiveShadow = true;
    return m;
  }
  function metalSpacer(w: number) {
    return sleeve(bore, metalR, w, matMetal, 0.04);
  }
  function rubberSpacer(w: number) {
    const g = new THREE.Group();
    g.add(sleeve(bore, rHub, w, matHub, 0.02));
    g.add(sleeve(rHub, rubberR, w * 0.96, matRubber, 0.0));
    return g;
  }
  function clampPart(w: number) {
    return sleeve(bore, clampR, w, matClamp, 0.1);
  }

  // 5 pockets, each split into 2 spacers (combination from inventory)
  const pockets = [{ split: [60, 40] }, { split: [50, 25] }, { split: [50, 30] }, { split: [60, 40] }, { split: [50, 25] }];
  const KW = 0.12;
  const DR = 0.35;
  const ER = 0.3;
  type LayoutItem = { type: "clamp" | "knife" | "spacer"; w: number; x: number };
  function buildLayout() {
    const L: LayoutItem[] = [];
    let x = 0;
    const cw = 0.5;
    L.push({ type: "clamp", w: cw, x: x + cw / 2 });
    x += cw + DR;
    for (let p = 0; p < pockets.length; p++) {
      L.push({ type: "knife", w: KW, x: x + KW / 2 });
      x += KW;
      const sp = pockets[p].split;
      for (let s = 0; s < sp.length; s++) {
        const w = sp[s] * W;
        L.push({ type: "spacer", w: w, x: x + w / 2 });
        x += w;
      }
    }
    L.push({ type: "knife", w: KW, x: x + KW / 2 });
    x += KW;
    const rw = 20 * W;
    L.push({ type: "spacer", w: rw, x: x + rw / 2 });
    x += rw + ER;
    const lw = 0.5;
    L.push({ type: "clamp", w: lw, x: x + lw / 2 });
    x += lw;
    return { length: x, layout: L };
  }
  const data = buildLayout();
  const spanLen = data.length;
  const shaftLen = spanLen + 0.5;

  const GAP = 2.5;
  const topGroup = new THREE.Group();
  topGroup.position.y = GAP / 2;
  const botGroup = new THREE.Group();
  botGroup.position.y = -GAP / 2;
  botGroup.position.x = 0.1;
  scene.add(topGroup);
  scene.add(botGroup);
  const offsetX = -spanLen / 2;

  function shaftMesh() {
    const m = new THREE.Mesh(new THREE.CylinderGeometry(shaftR, shaftR, shaftLen, 56, 1, false), matShaft);
    m.rotation.z = Math.PI / 2;
    m.castShadow = true;
    m.receiveShadow = true;
    return m;
  }
  topGroup.add(shaftMesh());
  botGroup.add(shaftMesh());

  const animParts: { obj: THREE.Object3D; tx: number }[] = [];
  const entryX = spanLen / 2 + 5;
  function place(group: THREE.Group, isTop: boolean) {
    data.layout.forEach(function (it, idx) {
      let obj: THREE.Object3D;
      if (it.type === "knife") obj = knife(it.w);
      else if (it.type === "spacer") obj = isTop ? metalSpacer(it.w) : rubberSpacer(it.w);
      else obj = clampPart(it.w);
      const tx = it.x + offsetX;
      obj.userData.tx = tx;
      group.add(obj);
      if (idx === 0) {
        obj.position.x = tx;
        obj.visible = true;
        const ring = new THREE.Mesh(new THREE.TorusGeometry(clampR - 0.02, 0.035, 12, 56), matMark);
        ring.position.x = tx;
        ring.rotation.y = Math.PI / 2;
        group.add(ring);
      } else {
        obj.position.x = entryX;
        obj.visible = false;
        animParts.push({ obj: obj, tx: tx });
      }
    });
  }
  place(topGroup, true);
  place(botGroup, false);
  animParts.sort(function (a, b) {
    return a.tx - b.tx;
  });

  scene.add(new THREE.HemisphereLight(0xc6d4ea, 0x232a36, 0.5));
  const key = new THREE.DirectionalLight(0xfff4e8, 2.1);
  key.position.set(7, 12, 8);
  key.castShadow = true;
  key.shadow.mapSize.set(1536, 1536);
  key.shadow.camera.near = 1;
  key.shadow.camera.far = 50;
  key.shadow.camera.left = -12;
  key.shadow.camera.right = 12;
  key.shadow.camera.top = 8;
  key.shadow.camera.bottom = -8;
  key.shadow.bias = -0.0004;
  key.shadow.radius = 4;
  scene.add(key);
  const fill = new THREE.DirectionalLight(0xbcd0ff, 0.5);
  fill.position.set(-10, 3, -3);
  scene.add(fill);
  const rim = new THREE.DirectionalLight(0xffffff, 1.0);
  rim.position.set(-4, 7, -11);
  scene.add(rim);
  const warm = new THREE.PointLight(0xe8742c, 0.45, 55);
  warm.position.set(7, -3, 8);
  scene.add(warm);

  const floor = new THREE.Mesh(new THREE.PlaneGeometry(120, 120), new THREE.ShadowMaterial({ opacity: 0.28 }));
  floor.rotation.x = -Math.PI / 2;
  floor.position.y = -(GAP / 2 + knifeR + 0.25);
  floor.receiveShadow = true;
  scene.add(floor);

  const Lhalf = spanLen / 2 + 0.2;
  const Hhalf = GAP / 2 + knifeR + 0.12;
  let camDist = 16;
  function fit() {
    const w = wrap.clientWidth || 600;
    const h = wrap.clientHeight || 296;
    renderer.setSize(w, h, false);
    const aspect = w / h;
    camera.aspect = aspect;
    const fovY = (camera.fov * Math.PI) / 180;
    const tY = Math.tan(fovY / 2);
    const fovX = 2 * Math.atan(tY * aspect);
    const tX = Math.tan(fovX / 2);
    // fit the real extents (not a bounding sphere), with margin for the viewing tilt
    const dV = Hhalf / (0.68 * tY);
    const dW = Lhalf / (0.78 * tX);
    camDist = Math.max(dV, dW);
    camera.updateProjectionMatrix();
  }
  let ro: ResizeObserver | null = null;
  if (window.ResizeObserver) {
    ro = new ResizeObserver(fit);
    ro.observe(wrap);
  }
  window.addEventListener("resize", fit);
  fit();

  const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const PHASE = 0.6;
  const STAG = 0.08;
  const DUR = 0.46;
  const HOLD = 5.5;
  const aEnd = PHASE + (animParts.length - 1) * STAG + DUR;
  const CYCLE = aEnd + HOLD;
  function eo(p: number) {
    return 1 - Math.pow(1 - p, 3);
  }
  function seatAll() {
    animParts.forEach(function (a) {
      a.obj.position.x = a.tx;
      a.obj.visible = true;
    });
  }
  const aim = new THREE.Vector3(0, 0, 0);

  let prev = performance.now();
  const t0 = prev;
  let spin = 0;
  let started = false;
  let rafId = 0;
  function tick(now: number) {
    rafId = requestAnimationFrame(tick);
    const dt = Math.min(0.05, (now - prev) / 1000);
    prev = now;
    const el = (now - t0) / 1000;
    const tt = reduce ? CYCLE : el % CYCLE;

    if (!started && el > 0.15) {
      started = true;
      vpload.style.opacity = "0";
    }

    if (!reduce) {
      for (let i = 0; i < animParts.length; i++) {
        const a = animParts[i];
        const loc = tt - (PHASE + i * STAG);
        if (loc < 0) {
          a.obj.visible = false;
        } else {
          a.obj.visible = true;
          const p = Math.min(1, loc / DUR);
          a.obj.position.x = entryX + (a.tx - entryX) * eo(p);
        }
      }
      spin += dt;
      topGroup.rotation.x = spin * 0.45;
      botGroup.rotation.x = -spin * 0.45;
    } else {
      seatAll();
      topGroup.rotation.x = 0.35;
      botGroup.rotation.x = -0.35;
    }

    const ct = reduce ? 0 : el;
    const yaw = 0.22 + Math.sin(ct * 0.08) * 0.07;
    const pitch = 0.13 + Math.sin(ct * 0.14) * 0.02;
    camera.position.set(
      Math.sin(yaw) * Math.cos(pitch) * camDist,
      Math.sin(pitch) * camDist,
      Math.cos(yaw) * Math.cos(pitch) * camDist
    );
    camera.lookAt(aim);

    let fade = 1;
    if (!reduce) {
      if (tt > CYCLE - 0.45) fade = (CYCLE - tt) / 0.45;
      else if (tt < 0.45) fade = tt / 0.45;
    }
    renderer.toneMappingExposure = exposure * Math.max(0, Math.min(1, fade));
    renderer.render(scene, camera);
  }
  rafId = requestAnimationFrame(tick);

  return {
    setTheme: function (mode) {
      const dark = mode === "dark";
      exposure = dark ? 1.0 : 0.95;
      matRubber.emissiveIntensity = dark ? 0.32 : 0.0;
      // Dark mode keeps the original bright steel; light mode darkens the steel
      // base colors a few shades so they hold contrast against the white panel.
      if (dark) {
        matShaft.color.setHex(0x8d949c);
        matKnife.color.setHex(0xccd2d9);
        matMetal.color.setHex(0x9aa1a9);
      } else {
        matShaft.color.setHex(0x6a7077);
        matKnife.color.setHex(0x9aa2ab);
        matMetal.color.setHex(0x747a82);
      }
    },
    dispose: function () {
      cancelAnimationFrame(rafId);
      if (ro) ro.disconnect();
      window.removeEventListener("resize", fit);
      renderer.dispose();
    },
  };
}

/**
 * Every visible string on the card, so a localized page can render it in its
 * own language (the Russian hub does). Defaults are the English homepage copy.
 */
export type HeroArborLabels = {
  cardTitle: string;
  ready: string;
  desc: string;
  mapTitle: string;
  datum: string;
  loading: string;
  top: string;
  fixed: string;
  loadingSide: string;
  bottom: string;
  metal: string;
  rubber: string;
  knife: string;
  clamp: string;
  inStock: string;
  error: string;
  assembly: string;
  rows: readonly [string, string, string, string, string];
  parts: string;
  partMetal: string;
  partRubber: string;
  partKnives: string;
  partShims: string;
};

export const HERO_ARBOR_LABELS_EN: HeroArborLabels = {
  cardTitle: "Step 4: Setup Complete",
  ready: "Ready",
  desc: "1250mm coil • 3 widths • 14 strips",
  mapTitle: "Arbor Stack Map",
  datum: "Datum",
  loading: "Loading",
  top: "Top (Metal)",
  fixed: "Fixed →",
  loadingSide: "← Loading",
  bottom: "Bottom (Rubber)",
  metal: "Metal",
  rubber: "Rubber",
  knife: "Knife",
  clamp: "Clamp",
  inStock: "All 23 parts in stock",
  error: "Error:",
  assembly: "Assembly",
  rows: ["CR → 25 end", "Knife → 100 × 5", "Knife → 75 × 4", "Knife → 80 × 5", "25 end → CR"],
  parts: "Parts",
  partMetal: "Metal",
  partRubber: "Rubber",
  partKnives: "Knives",
  partShims: "Shims",
};

export default function HeroArborCard({
  labels = HERO_ARBOR_LABELS_EN,
}: {
  labels?: HeroArborLabels;
} = {}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const loadRef = useRef<HTMLDivElement>(null);
  const arborRef = useRef<ArborHandle | null>(null);
  const [theme, setTheme] = useState<"light" | "dark">("light");

  // Mirror the site theme (dark class on <html>) onto the card and the scene.
  useEffect(() => {
    const root = document.documentElement;
    const compute = (): "light" | "dark" => (root.classList.contains("dark") ? "dark" : "light");
    setTheme(compute());
    const obs = new MutationObserver(() => {
      const t = compute();
      setTheme(t);
      arborRef.current?.setTheme(t);
    });
    obs.observe(root, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);

  // Initialise the WebGL scene after hydration, off the LCP path. three is
  // dynamically imported so it never weighs down the initial bundle.
  //
  // AND NOT UNTIL THE CARD IS ACTUALLY WORTH DRAWING. three is 582 KB raw /
  // 145 KB on the wire — measured, the single largest download the homepage
  // makes, bigger than every first-party script chunk put together. Being a
  // dynamic import keeps it out of the initial bundle but did NOT stop it:
  // the effect ran on hydration, so every visitor fetched it and paid for the
  // WebGL scene build regardless of whether the canvas was anywhere near the
  // screen. On mobile the card is `order-2`, below the whole hero copy, so
  // that is a phone spending 145 KB and a scene init on something the reader
  // has not scrolled to yet — precisely when bandwidth and main thread are
  // scarcest.
  //
  // So the import waits for the canvas to come within 400px of the viewport.
  // Anyone who sees the card gets exactly what they got before, a little
  // earlier than they can perceive; anyone who bounces off the hero never
  // pays for it. The observer disconnects the moment it fires, so this cannot
  // start the scene twice.
  useEffect(() => {
    let handle: ArborHandle | undefined;
    let cancelled = false;
    let started = false;

    const start = () => {
      if (started || cancelled) return;
      started = true;
      void (async () => {
        const THREE = await import("three");
        if (cancelled || !canvasRef.current || !wrapRef.current || !loadRef.current) return;
        handle = createArbor(THREE, canvasRef.current, wrapRef.current, loadRef.current);
        handle.setTheme(document.documentElement.classList.contains("dark") ? "dark" : "light");
        arborRef.current = handle;
      })();
    };

    const wrap = wrapRef.current;

    // No IntersectionObserver (or no element yet) => behave exactly as before
    // rather than risk a card that never draws.
    if (!wrap || typeof IntersectionObserver === "undefined") {
      start();
      return () => {
        cancelled = true;
        handle?.dispose();
        arborRef.current = null;
      };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          observer.disconnect();
          start();
        }
      },
      { rootMargin: "400px" }
    );
    observer.observe(wrap);

    return () => {
      cancelled = true;
      observer.disconnect();
      handle?.dispose();
      arborRef.current = null;
    };
  }, []);

  return (
    <div className={styles.root} data-theme={theme}>
      <div className={styles.device}>
        <div className={styles.card}>
          <div className={styles.cardBar}>
            <span className={styles.dots}>
              <i></i>
              <i></i>
              <i></i>
            </span>
            <span className={styles.cardTitle}>{labels.cardTitle}</span>
            <span className={styles.rdyBadge}>{labels.ready}</span>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.summary}>
              <span className={styles.readyPill}>
                <span className="pip"></span>
                {labels.ready}
              </span>
              <span className={styles.desc}>{labels.desc}</span>
            </div>

            <div className={styles.panel}>
              <div className={styles.panelHead}>
                <span className="t">{labels.mapTitle}</span>
                <span className="meta">
                  <span className="dot"></span>
                  {labels.datum} &nbsp; {labels.loading} <span className="mm">0.04mm</span>
                </span>
              </div>

              <div className={styles.vpLabels}>
                <span>{labels.top}</span>
                <span>{labels.fixed}</span>
              </div>
              <div className={styles.canvasWrap} ref={wrapRef}>
                <canvas ref={canvasRef}></canvas>
                <div className={styles.vpLoad} ref={loadRef}>
                  {labels.loading}
                </div>
              </div>
              <div className={`${styles.vpLabels} bottom`}>
                <span>{labels.loadingSide}</span>
                <span>{labels.bottom}</span>
              </div>

              <div className={styles.legend}>
                <span>
                  <i className={styles.lMetal}></i>
                  {labels.metal}
                </span>
                <span>
                  <i className={styles.lRubber}></i>
                  {labels.rubber}
                </span>
                <span>
                  <i className={styles.lKnife}></i>
                  {labels.knife}
                </span>
                <span>
                  <i className={styles.lClamp}></i>
                  {labels.clamp}
                </span>
              </div>

              <div className={styles.status}>
                <span className="ins">
                  <span className="pip"></span>
                  {labels.inStock}
                </span>
                <span className="err">
                  {labels.error} <b>&plusmn;0&micro;m</b>
                </span>
              </div>
            </div>

            <div className={styles.bom}>
              <div className="col asm">
                <div className="h">{labels.assembly}</div>
                {labels.rows.map((row, i) => (
                  <div className="row" key={row}>
                    <span className="n">{i + 1}</span>
                    <span className="lbl">{row}</span>
                  </div>
                ))}
              </div>
              <div className="col parts">
                <div className="h">{labels.parts}</div>
                <div className="row">
                  <span className="lbl">{labels.partMetal}</span>
                  <span className="qty">14</span>
                </div>
                <div className="row">
                  <span className="lbl">{labels.partRubber}</span>
                  <span className="qty">12</span>
                </div>
                <div className="row">
                  <span className="lbl">{labels.partKnives}</span>
                  <span className="qty">13</span>
                </div>
                <div className="row">
                  <span className="lbl">{labels.partShims}</span>
                  <span className="qty">4</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
